'use client'

// eslint-disable-next-line no-restricted-imports
import { usePathname as useNextPathname } from 'next/navigation'
import styled, { css } from 'styled-components'

import { Box, ContentText, IconChevronRight, spacing } from '@fortum/elemental-ui'

import { Icon } from '@/shared/components/Icon'
import { StateButton } from '@/shared/components/StateButton'
import { StyledLink } from '@/shared/components/StyledLink'
import type { ColumnMenuEntry, LinkEntry } from '@/shared/contentful/types'
import { isTypeColumnMenu, isTypeLink } from '@/shared/contentful/types/contentTypeGuards'
import { getLinkEntryUrl } from '@/shared/contentful/utils'
import { useTheme } from '@/shared/hooks/useTheme'
import { useLocale } from '@/shared/locale'

type SliderMenuLinkProps = {
  item: LinkEntry | ColumnMenuEntry
  /** true if this menu’s panel is currently open */
  expanded: boolean
  /** callback to open/close this panel */
  onToggle: () => void
}

export const SliderMenuLink = ({ item, expanded, onToggle }: SliderMenuLinkProps) => {
  const theme = useTheme()
  const locale = useLocale()
  const pathname = useNextPathname()

  const isLink = isTypeLink(item)
  const isColumn = isTypeColumnMenu(item)
  const label = isLink ? item.label! : item.title!
  const href = isLink
    ? (getLinkEntryUrl(item, locale, {
        asString: true,
        withLocalePrefixIfNeeded: true,
      }) as string)
    : undefined

  const isActiveLink = !!(isLink && href === pathname)
  const isActive = isActiveLink || expanded

  if (isLink && href) {
    return (
      <StyledLink
        href={href}
        onClick={onToggle}
        display="block"
        noUnderline
        noUnderlineHover
        data-testid={`mobile-header-menu-item-${label}`}
        aria-current={isActiveLink ? 'page' : undefined}
      >
        <StateButton
          as="div"
          status="plain"
          tabIndex={-1}
          color={theme.colors.textPrimary}
          width="100%"
          p={0}
          isActive={isActive}
        >
          <EffectBox $isActive={isActive}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              ph={spacing.xs}
              pv={spacing.xxs}
            >
              <ContentText>{label}</ContentText>
              {isColumn && <Icon icon={IconChevronRight} />}
            </Box>
          </EffectBox>
        </StateButton>
      </StyledLink>
    )
  }

  // Otherwise it’s a collapsible menu header → keep as <button>
  return (
    <StateButton
      role="button"
      status="plain"
      color={theme.colors.textPrimary}
      width="100%"
      p={0}
      onClick={onToggle}
      isActive={isActive}
      aria-expanded={expanded}
      aria-controls={`mobile-menu-${item.name}`}
      data-testid={`mobile-header-menu-item-${label}`}
    >
      <EffectBox $isActive={isActive}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          ph={spacing.xs}
          pv={spacing.xxs}
        >
          <ContentText>{label}</ContentText>
          {isColumn && <Icon icon={IconChevronRight} />}
        </Box>
      </EffectBox>
    </StateButton>
  )
}

const EffectBox = styled(Box)<{ $isActive?: boolean }>`
  ${({ $isActive, theme }) => css`
    ${$isActive
      ? `
          div {
            border-left: ${spacing.xxxxs} solid ${theme.colors.brandPrimary};
            background-color: ${theme.colors.brandPrimary}10;
          }
        `
      : ''}
    &:hover,
    &:focus,
    &:active {
      background-color: ${theme.colors.brandPrimary}10;

      p {
        color: ${theme.colors.textPrimary};
        background: none;
      }
    }
  `}
`
